// Jobs (admin only). The "Teams close" panel (contracts/phase3.md "Weekly close",
// POST /admin/v1/jobs/teams-close): pick a week, dry-run it to see the planned
// promotions, relegations and dormant teams, then confirm to run it. Audited.

import { TeamEmblem } from "@lumen/team-emblem/react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ApiError } from "../api/client";
import { useDivisions, useTeamsClose } from "../api/queries";
import type { TeamMove, TeamsCloseResult } from "../api/schemas";
import { useAuth, useCan } from "../auth/AuthContext";
import { tableStyles } from "../components/table";
import { Button, Card, Chip, ConfirmDialog, EmptyState, Field, InlineError, PageHeader, Select, Stat } from "../components/ui";
import { formatDateTime } from "../lib/dates";
import { fmtInt } from "../lib/format";
import { ordinal, WEEK_STATUS_LABEL, WEEK_STATUS_TONE, weekRange } from "../lib/teams";
import styles from "./pages.module.css";
import t from "./teams.module.css";

export function JobsPage() {
  const canRun = useCan("jobs.run");
  const { admin } = useAuth();
  return (
    <>
      <PageHeader
        title="Jobs"
        meta={
          canRun ? (
            <span>Scheduled jobs you can preview and run by hand. Every run is written to the audit log.</span>
          ) : (
            <span className={styles.readOnly} data-testid="read-only">
              Read-only · running jobs needs the admin role (you are {admin?.role})
            </span>
          )
        }
      />
      <TeamsClosePanel enabled={canRun} />
    </>
  );
}

function TeamsClosePanel({ enabled }: { enabled: boolean }) {
  const [params, setParams] = useSearchParams();
  const divisions = useDivisions(null);
  // Ended weeks only: the week in play can't be closed.
  const weeks = (divisions.data?.weeks ?? []).filter((w) => w.status !== "open");
  // Default: a week known to be waiting, else the last one that ended.
  const fallback = weeks.find((w) => w.status === "pending")?.week ?? weeks[0]?.week ?? "";
  const week = params.get("week") && /^\d{4}-W\d{2}$/.test(params.get("week")!) ? params.get("week")! : fallback;
  const [plan, setPlan] = useState<TeamsCloseResult | null>(null);
  const [done, setDone] = useState<TeamsCloseResult | null>(null);
  const [confirming, setConfirming] = useState(false);
  const dry = useTeamsClose();
  const run = useTeamsClose();
  const pick = (w: string) => {
    setParams(w ? { week: w } : {}, { replace: true });
    setPlan(null);
    setDone(null);
    dry.reset();
    run.reset();
  };
  const shown = plan && plan.week === week ? plan : null;
  const status = shown?.status ?? weeks.find((w) => w.week === week)?.status ?? null;
  const runnable = enabled && shown !== null && shown.status !== "open" && shown.status !== "closed" && !done;

  return (
    <Card
      title="Teams close"
      actions={<Chip tone="muted">Mondays 00:05 UTC</Chip>}
      className={t.jobCard}
    >
      <p className={styles.prose}>
        Finalises the week's team scores, promotes ranks 1–3 a tier (not from tier 1), relegates 16–18 (not from the lowest tier), sends
        teams with no finished set for three weeks dormant, rebalances the divisions and snapshots the tables. A dry run changes nothing.
      </p>
      {!enabled ? (
        <p className={styles.footNote}>Only admins can preview or run this job.</p>
      ) : (
        <>
          <div className={t.jobControls}>
            <Field label="Week">
              <Select value={week} onChange={(e) => pick(e.target.value)} aria-label="Week to close" style={{ width: 340 }} disabled={!weeks.length}>
                {weeks.map((w) => (
                  <option key={w.week} value={w.week}>
                    {`${w.week} · ${weekRange(w.week)}${w.status ? ` · ${WEEK_STATUS_LABEL[w.status]}` : ""}`}
                  </option>
                ))}
              </Select>
            </Field>
            <Button
              variant="secondary"
              busy={dry.isPending}
              disabled={!week}
              onClick={() => {
                setDone(null);
                run.reset();
                dry.mutate({ week, dryRun: true }, { onSuccess: (r) => setPlan(r) });
              }}
            >
              Dry run
            </Button>
            <Button variant="primary" disabled={!runnable} onClick={() => setConfirming(true)} title={!shown ? "Dry-run the week first" : undefined}>
              Run close…
            </Button>
            {status ? <Chip tone={WEEK_STATUS_TONE[status]}>{WEEK_STATUS_LABEL[status]}</Chip> : null}
          </div>
          <InlineError error={dry.error} />

          {done ? (
            <p className={t.success} role="status" data-testid="close-done">
              Closed {done.week}
              {done.closedAt ? ` at ${formatDateTime(done.closedAt)}` : ""}: {done.counts.promoted} promoted, {done.counts.relegated} relegated,{" "}
              {done.counts.dormant} dormant. <Link to={`/divisions`}>See this week's tables →</Link>
            </p>
          ) : null}

          {shown ? <Plan result={done ?? shown} /> : dry.isPending ? null : <p className={styles.faint}>Pick a week and dry-run it to see what the close would change.</p>}
        </>
      )}

      <ConfirmDialog
        open={confirming}
        title={`Close ${week}?`}
        confirmLabel="Run close"
        tone="danger"
        busy={run.isPending}
        error={run.error}
        onCancel={() => setConfirming(false)}
        onConfirm={() =>
          shown &&
          run.mutate(
            { week, dryRun: false, token: shown.token },
            {
              onSuccess: (r) => {
                setDone(r);
                setConfirming(false);
              },
              onError: (e) => {
                // The week changed under us (someone else closed it): refresh the plan.
                if (e instanceof ApiError && e.status === 409) dry.mutate({ week, dryRun: true }, { onSuccess: (r) => setPlan(r) });
              },
            },
          )
        }
      >
        {shown ? (
          <div className={styles.prose}>
            <p>
              {shown.counts.promoted} teams go up, {shown.counts.relegated} go down and {shown.counts.dormant} go dormant across{" "}
              {shown.divisions ?? "the"} divisions. Players get their weekly results and XP. This can't be undone.
            </p>
            <p className={styles.faint}>Written to the audit log as job.teams_close.</p>
          </div>
        ) : null}
      </ConfirmDialog>
    </Card>
  );
}

function Plan({ result }: { result: TeamsCloseResult }) {
  const note =
    result.status === "open"
      ? "This week hasn't ended: the preview uses the standings so far, and it can't be run until Monday."
      : result.status === "closed" && result.dryRun
        ? `Already closed${result.closedAt ? ` ${formatDateTime(result.closedAt)}` : ""}. These are the moves it made.`
        : result.dryRun
          ? "Dry run: nothing has changed yet."
          : null;
  return (
    <div className={t.plan} data-testid="close-plan">
      {note ? <p className={t.planNote} data-status={result.status ?? undefined}>{note}</p> : null}
      <div className={styles.statsRow}>
        <Stat label="Divisions" value={result.divisions ?? "—"} />
        <Stat label="Teams" value={result.teams ?? "—"} />
        <Stat label="Promoted" value={result.counts.promoted} />
        <Stat label="Relegated" value={result.counts.relegated} />
        <Stat label="Stayed" value={result.counts.stayed ?? "—"} />
        <Stat label="Dormant" value={result.counts.dormant} />
      </div>
      {Object.keys(result.xp).length || Object.keys(result.badges).length ? (
        <dl className={t.awards} data-testid="close-awards">
          {Object.keys(result.xp).length ? (
            <div>
              <dt>{result.dryRun ? "XP to award" : "XP awarded"}</dt>
              <dd>
                {Object.entries(result.xp).map(([source, n]) => (
                  <span key={source} className={t.award}>
                    <span className="mono">{fmtInt(n)}</span> {source.replace(/_/g, " ")}
                  </span>
                ))}
              </dd>
            </div>
          ) : null}
          {Object.keys(result.badges).length ? (
            <div>
              <dt>{result.dryRun ? "Badge candidates" : "Badges"}</dt>
              <dd>
                {Object.entries(result.badges).map(([badge, n]) => (
                  <span key={badge} className={t.award}>
                    <span className="mono">{fmtInt(n)}</span> {badge.replace(/_/g, " ")}
                  </span>
                ))}
              </dd>
            </div>
          ) : null}
        </dl>
      ) : null}
      <div className={t.planGrid}>
        <MoveTable title="Promotions" moves={result.promotions} kind="up" />
        <MoveTable title="Relegations" moves={result.relegations} kind="down" />
      </div>
      {result.dormant.length ? <MoveTable title="Going dormant" moves={result.dormant} kind="dormant" /> : null}
    </div>
  );
}

function MoveTable({ title, moves, kind }: { title: string; moves: TeamMove[]; kind: "up" | "down" | "dormant" }) {
  return (
    <section className={t.moves} data-testid={`moves-${kind}`} aria-label={title}>
      <h3 className={t.movesTitle}>
        {title} <span className="monoLabel">{moves.length}</span>
      </h3>
      {moves.length === 0 ? (
        <EmptyState title="None">{kind === "dormant" ? "Every team played." : "No team moves this way."}</EmptyState>
      ) : (
        <div className={tableStyles.wrap}>
          <table className={tableStyles.table}>
            <thead>
              <tr>
                <th>Team</th>
                <th>{kind === "dormant" ? "Leaves" : "Move"}</th>
                <th data-align="right">Rank</th>
                <th data-align="right">Score</th>
              </tr>
            </thead>
            <tbody>
              {moves.map((m) => (
                <tr key={m.teamId} data-move={kind}>
                  <td>
                    <Link to={`/teams/${m.teamId}`} className={t.teamCell}>
                      {m.emblem ? <TeamEmblem emblem={m.emblem} size={18} /> : null}
                      <span className={t.teamName}>{m.name}</span>
                    </Link>
                  </td>
                  <td className={tableStyles.mono}>
                    {kind === "dormant" ? (
                      `Division ${m.tier ?? "?"}${m.weeksInactive ? ` · ${m.weeksInactive} wk idle` : ""}`
                    ) : (
                      <>
                        D{m.tier ?? "?"}
                        {m.index !== null ? `·${m.index}` : ""} <span className={t.arrow}>{kind === "up" ? "↑" : "↓"}</span> D{m.toTier ?? "?"}
                      </>
                    )}
                  </td>
                  <td data-align="right" className="mono">
                    {m.rank ? ordinal(m.rank) : "—"}
                  </td>
                  <td data-align="right" className="mono">
                    {m.score === null ? "—" : fmtInt(m.score)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
